import Phaser from 'phaser';

import { BlackjackMainScene } from '../../scenes/blackjack-main-scene';
import { Subscription } from 'rxjs';
import { ResizeMetrics } from '../../model/resize-metrics';
// import { DebugContainerVisual } from '../debug/debug-container-visual';
import { Blackjack, GameEvent } from 'game-logic';
import { CardView } from '../cards/card-view';
import { CroupierCardStackViewModel } from './croupier-card-stack-view-model';
import { delay } from 'src/games/common/helper';
import { UxMetrics } from '../../model/ux-metrics';

export class CroupierCardStackView extends Phaser.GameObjects.Container {
  private currentResizeMetrics?: ResizeMetrics;
  private subscriptionGroup: Subscription;

  private cardViews: Map<number, CardView> = new Map();
  private destroyTweens: Phaser.Tweens.Tween[] = [];

  constructor(
    scene: BlackjackMainScene,
    x: number,
    y: number,
    private viewModel: CroupierCardStackViewModel,
  ) {
    super(scene, x, y);

    // new DebugContainerVisual(scene, this);

    this.subscriptionGroup = new Subscription();

    this.setName(CroupierCardStackView.name);
    scene.add.existing(this);

    this.subscriptionGroup.add(
      this.viewModel.cardViewModelChanges$.subscribe((change) => {
        if (change.type === 'addopen' || change.type === 'addhidden') {
          const newPositions = this.calculateNewCardPositions(this.cardViews.size + 1);
          this.repositionCards(newPositions, true);

          // Add the new card view
          const cardView = new CardView(scene, 0, 0, change.viewModel);
          if (this.currentResizeMetrics) {
            cardView.onResize(this.currentResizeMetrics);
          }
          this.add(cardView);

          const cardIndex = this.cardViews.size;
          this.cardViews.set(cardView.cardIndex, cardView);

          const startPt = this.globalStartPosition();
          const localStartPt = new Phaser.Math.Vector2();
          this.getLocalPoint(startPt.x, startPt.y, localStartPt);
          cardView.setPosition(localStartPt.x, localStartPt.y);

          const endPt = this.localEndPosition(cardIndex, this.cardViews.size);

          let openTriggered = false;
          this.scene.tweens.add({
            targets: [cardView],
            x: endPt.x,
            y: endPt.y,
            duration: change.delay,
            ease: Phaser.Math.Easing.Quintic.InOut,
            onUpdate: (tween) => {
              if (!openTriggered && tween.progress >= 0.8) {
                openTriggered = true;
                if (change.type === 'addopen') {
                  cardView.flipCard(true, true);
                }
              }
            },
          });
        } else if (change.type === 'flip') {
          const cardView = this.cardViews.get(change.viewModel.cardIndex);
          cardView?.flipCard(true, true);
        } else if (change.type === 'remove') {
          const cardView = this.cardViews.get(change.viewModel.cardIndex);
          if (cardView) {
            const disposePt = this.globalDisposePosition();
            const localDisposePt = new Phaser.Math.Vector2();
            this.getLocalPoint(disposePt.x, disposePt.y, localDisposePt);

            const destroyTween = this.scene.tweens.add({
              targets: [cardView],
              x: localDisposePt.x,
              y: localDisposePt.y,
              duration: change.delay,
              ease: Phaser.Math.Easing.Quadratic.Out,
              onComplete: () => {
                cardView.destroy();
              },
            });
            this.destroyTweens.push(destroyTween);
          }
          this.cardViews.delete(change.viewModel.cardIndex);
        }
      }),
    );
    this.subscriptionGroup.add(
      viewModel.peekAtCard$.subscribe((data) => {
        const cardView = this.cardViews.get(data.cardIndex);
        if (cardView) {
          this.scene.tweens.add({
            targets: [cardView],
            scale: 1.2,
            y: cardView.y - cardView.height * 0.15,
            duration: data.delay * 0.5,
            ease: 'Power2',
            yoyo: true,
            hold: data.pause, // Pause before reversing
          });
        }
      }),
    );
    this.subscriptionGroup.add(
      viewModel.logicEventQueue$.subscribe((eventQueue) => {
        eventQueue?.subscribe(this.handleLogicEvents.bind(this));
      }),
    );
  }

  override destroy(_fromScene?: boolean | undefined): void {
    this.subscriptionGroup.unsubscribe();

    // Stop any tweens which are destroying child views
    this.destroyTweens.forEach((tween) => {
      tween.stop();
    });

    super.destroy(_fromScene);
  }

  public onResize(resizeMetrics: ResizeMetrics) {
    this.cardViews.forEach((cardView) => cardView.onResize(resizeMetrics));

    this.currentResizeMetrics = resizeMetrics;
    this.refreshLayout();
  }

  private refreshLayout(): void {
    if (!this.currentResizeMetrics) {
      return;
    }
    const layoutConfig = this.currentResizeMetrics.layoutConfig;
    const scalingMetrics = this.currentResizeMetrics.scalingMetrics;

    const boxScale = 0.8;
    const controlsWidth = layoutConfig.bettingBoxWidth * boxScale * scalingMetrics.scaleFactor;
    const controlsHeight = layoutConfig.bettingBoxHeight * boxScale * scalingMetrics.scaleFactor;

    this.setSize(controlsWidth, controlsHeight);

    this.layoutCards();
  }

  private layoutCards() {
    Array.from(this.cardViews.values()).forEach((cardView, index) => {
      const endPt = this.localEndPosition(index, this.cardViews.size);
      cardView.setPosition(endPt.x, endPt.y);
    });
  }

  private calculateNewCardPositions(totalCards: number): {
    cardView: CardView;
    endPt: {
      x: number;
      y: number;
    };
  }[] {
    return Array.from(this.cardViews.values()).map((cardView, index) => {
      // Get the new end point assuming one more card is being added
      const endPt = this.localEndPosition(index, totalCards);
      return { cardView, endPt };
    });
  }

  private async repositionCards(
    positions: {
      cardView: CardView;
      endPt: {
        x: number;
        y: number;
      };
    }[],
    animated: boolean,
  ): Promise<void> {
    for (const position of positions) {
      if (!animated) {
        position.cardView.setPosition(position.endPt.x, position.endPt.y);
      } else {
        // Animate
        this.scene.tweens.add({
          targets: [position.cardView],
          x: position.endPt.x,
          y: position.endPt.y,
          duration: 200,
          ease: Phaser.Math.Easing.Quartic.InOut,
        });
        await delay(80);
      }
    }
  }

  private async handleLogicEvents(event: GameEvent<Blackjack.BlackjackEvents>): Promise<void> {
    // Don't process instant events
    if (event.instant) {
      return;
    }

    switch (event.type) {
      case 'onDrawDeckCroupier':
        if (event.payload.croupier.allFaceCards.length > 2) {
          await delay(UxMetrics.CroupierPreCardDelay);
        }
        await this.viewModel.drawDeckCroupier(
          event.payload.croupier,
          event.payload.cardIndex,
          true,
        );
        break;
      case 'onDrawDeckCroupierHidden':
        await this.viewModel.drawDeckCroupier(
          event.payload.croupier,
          event.payload.cardIndex,
          false,
        );
        break;
      case 'onRevealDeckCroupier':
        await delay(UxMetrics.CardPreRevealDelay);
        await this.viewModel.flipCroupierCard(event.payload.croupier, event.payload.cardIndex);
        break;
      case 'onCroupierCleared':
        await this.viewModel.clearAllCroupierCards();
        break;
      case 'onCroupierPeek':
        await this.viewModel.peekBlackjack(event.payload.croupier);
        break;
      default:
        break;
    }
  }

  private globalStartPosition(): { x: number; y: number } {
    const scaleFactor = this.currentResizeMetrics?.scalingMetrics.scaleFactor ?? 1;
    const scaledOffsetX = this.currentResizeMetrics?.scalingMetrics.scaledOffsetX ?? 0;

    const boxScale = 0.8;

    const controlsWidth = this.viewModel.layoutConfig.bettingBoxWidth * boxScale;
    const startX =
      this.viewModel.layoutConfig.boardWidth -
      controlsWidth * 0.5 -
      this.viewModel.layoutConfig.paddingSm;
    const startY = this.viewModel.layoutConfig.boardHeight * 0.15;

    return { x: startX * scaleFactor + scaledOffsetX, y: startY * scaleFactor };
  }

  private localEndPosition(cardIndex: number, cardCount: number): { x: number; y: number } {
    const scaleFactor = this.currentResizeMetrics?.scalingMetrics.scaleFactor ?? 1;

    const xCardOffset = cardIndex * this.viewModel.layoutConfig.cardStackSpacing;
    const xStackOffset = (cardCount - 1) * this.viewModel.layoutConfig.cardStackSpacing * 0.5;

    return { x: (xCardOffset - xStackOffset) * scaleFactor, y: 0 };
  }

  private globalDisposePosition(): { x: number; y: number } {
    const scaleFactor = this.currentResizeMetrics?.scalingMetrics.scaleFactor ?? 1;
    const scaledOffsetX = this.currentResizeMetrics?.scalingMetrics.scaledOffsetX ?? 0;

    const startX = this.viewModel.layoutConfig.boardWidth * -0.25;
    const startY = this.viewModel.layoutConfig.boardHeight * 0.05;

    return { x: startX * scaleFactor + scaledOffsetX, y: startY * scaleFactor };
  }
}
